import { tr } from '../i18n/i18n.ts';
import { LocalNotifications, type LocalNotificationSchema } from '@capacitor/local-notifications';
import { stableInt31, type NotificationPlan, type PlannedNotification } from '@autoday/core';
import { DEFAULT_NOTIFICATION_OUTPUT, type ActionNeeded, type ChannelStatus, type NotificationOutput, type NotificationTransport, type ScheduleReport } from './types.ts';

export const ACTION_TYPE = 'AUTODAY_REMINDER';
export const CHANNEL_PUBLIC = 'autoday-reminders-v2';
export const CHANNEL_PRIVATE = 'autoday-reminders-private-v2';

/** Android låser kanalens ljud och vibration när den skapas. Ett nytt kombinations-ID behövs vid byte. */
export function channelId(privateContent: boolean, output: NotificationOutput): string {
  return `${privateContent ? CHANNEL_PRIVATE : CHANNEL_PUBLIC}-${output.sound ? 'sound' : 'silent'}-${output.vibration ? 'vibrate' : 'still'}`;
}

/**
 * OS-schemalagda lokala notiser via @capacitor/local-notifications.
 *
 * Android: AlarmManager (setExactAndAllowWhileIdle om SCHEDULE_EXACT_ALARM är beviljat, annars inexakt).
 * Pluginet återställer schemat efter omstart (BOOT_COMPLETED). Force-stop tar bort alla alarm tills
 * appen öppnas igen – det är en Android-regel och redovisas i Notisstatus.
 * iOS: UNUserNotificationCenter, max 64 väntande notiser per app.
 *
 * Vi öppnar ALDRIG inställningar automatiskt vid schemaläggning (isExactNotification sätts utifrån
 * kontrollerat tillstånd) – det sker bara när användaren trycker på knappen.
 */
export class CapacitorLocalTransport implements NotificationTransport {
  readonly channel: 'android-local' | 'ios-local';
  readonly capacity: number;
  private initialized = false;
  private channels = new Set<string>();

  /**
   * accountTag: vilken lokal profil notiserna tillhör ('guest' eller kontots opaka markering). Följer med
   * i varje notis så att en gammal notis aldrig öppnar ett annat kontos data efter utloggning.
   */
  constructor(
    private os: 'android' | 'ios',
    private accountTag: string = 'guest',
  ) {
    this.channel = os === 'android' ? 'android-local' : 'ios-local';
    // iOS: 64 är systemgränsen; 4 platser reserveras för snooze/test. Android: håll antalet alarm rimligt.
    this.capacity = os === 'ios' ? 60 : 150;
  }

  private async init(output: NotificationOutput): Promise<void> {
    if (!this.initialized) await LocalNotifications.registerActionTypes({
      types: [
        {
          id: ACTION_TYPE,
          actions: [
            { id: 'open', title: tr('Öppna', 'Open'), foreground: true },
            { id: 'snooze10', title: tr('Påminn om 10 minuter', 'Remind me in 10 minutes') },
            { id: 'snooze60', title: tr('Påminn om 1 timme', 'Remind me in 1 hour') },
          ],
        },
      ],
    });
    this.initialized = true;
    if (this.os === 'android') {
      // Visibility: 1 = public, 0 = private (innehåll döljs på säker låsskärm).
      const mode = output.sound && output.vibration ? tr('ljud och vibration', 'sound and vibration')
        : output.sound ? tr('ljud', 'sound') : output.vibration ? tr('vibration', 'vibration') : tr('tyst', 'silent');
      for (const privateContent of [false, true]) {
        const id = channelId(privateContent, output);
        if (this.channels.has(id)) continue;
        await LocalNotifications.createChannel({
          id,
          name: `${privateContent ? tr('Privata påminnelser', 'Private reminders') : tr('Påminnelser', 'Reminders')} (${mode})`,
          importance: 4,
          visibility: privateContent ? 0 : 1,
          description: privateContent ? tr('Innehåll dolt på låsskärmen', 'Content hidden on the lock screen') : tr('Påminnelser för aktiviteter', 'Reminders for activities'),
          // Pluginet kan inte ange Android-kanalens ljud till null. Den tysta ljudfilen
          // låter därför kanalen förbli prioriterad utan att spela ett hörbart ljud.
          sound: output.sound ? 'autoday_chime.wav' : 'autoday_silent.wav',
          vibration: output.vibration,
        });
        this.channels.add(id);
      }
    }
  }

  async status(): Promise<ChannelStatus> {
    const perm = await LocalNotifications.checkPermissions();
    const permission = perm.display === 'granted' ? 'granted' : perm.display === 'denied' ? 'denied' : 'prompt';
    let exact: ChannelStatus['exactAlarms'] = 'n/a';
    if (this.os === 'android') {
      try {
        exact = (await LocalNotifications.checkExactNotificationSetting()).exact_alarm === 'granted' ? 'granted' : 'denied';
      } catch {
        exact = 'denied';
      }
    }
    const limitations: string[] = [];
    const actions: ChannelStatus['actions'] = [];
    if (permission !== 'granted') {
      limitations.push(tr('Notiser är inte tillåtna – inga påminnelser visas.', 'Notifications are not allowed – no reminders are shown.'));
      actions.push(permission === 'denied' ? { id: 'open-app-settings', label: tr('Öppna appinställningar', 'Open app settings') } : { id: 'request-permission', label: tr('Tillåt notiser', 'Allow notifications') });
    }
    if (this.os === 'android') {
      if (exact === 'denied') {
        limitations.push(tr('Exakta larm är inte tillåtna. Påminnelser kan komma flera minuter sent, särskilt i batterisparläge.', 'Exact alarms are not allowed. Reminders may arrive several minutes late, especially in battery saver mode.'));
        actions.push({ id: 'enable-exact-alarms', label: tr('Tillåt exakta larm', 'Allow exact alarms') });
      }
      limitations.push(tr('Om appen tvångsstoppas (Force stop) tar Android bort alla schemalagda påminnelser tills du öppnar AutoDay igen.', 'If the app is force-stopped, Android removes all scheduled reminders until you open AutoDay again.'));
      limitations.push(tr('Vissa tillverkares batterioptimering kan fördröja eller stoppa påminnelser. Testa med "Testa om 1 minut".', 'Some manufacturers’ battery optimisation can delay or stop reminders. Check with "Test in 1 minute".'));
    } else {
      limitations.push(tr('iOS tillåter högst 64 väntande notiser per app. AutoDay schemalägger de närmaste och fyller på när appen öppnas.', 'iOS allows at most 64 pending notifications per app. AutoDay schedules the nearest ones and tops up when the app is opened.'));
    }
    return {
      channel: this.channel,
      label: this.os === 'android' ? tr('Android – lokala notiser', 'Android – local notifications') : tr('iPhone – lokala notiser', 'iPhone – local notifications'),
      permission,
      exactAlarms: exact,
      backgroundCapable: permission === 'granted',
      limitations,
      actions,
    };
  }

  async requestPermission(): Promise<ChannelStatus> {
    await LocalNotifications.requestPermissions();
    return this.status();
  }

  async openSettings(id: ActionNeeded): Promise<void> {
    if (id === 'enable-exact-alarms' && this.os === 'android') await LocalNotifications.changeExactNotificationSetting();
    if (id === 'request-permission') await LocalNotifications.requestPermissions();
  }

  private toSchema(n: PlannedNotification, hideSensitive: boolean, exact: boolean, output: NotificationOutput): LocalNotificationSchema {
    const sensitiveHidden = hideSensitive && n.sensitive;
    // iOS saknar per-notis låsskärmsversion → visa den neutrala texten. Android: privat kanal döljer innehållet.
    const useGeneric = sensitiveHidden && this.os === 'ios';
    return {
      id: n.numericId,
      title: useGeneric ? n.publicTitle : n.title,
      body: useGeneric ? n.publicBody : n.body,
      schedule: { at: new Date(n.fireAt), allowWhileIdle: true },
      actionTypeId: ACTION_TYPE,
      ...(this.os === 'android' ? { channelId: channelId(sensitiveHidden, output), sound: output.sound ? 'autoday_chime.wav' : 'autoday_silent.wav', isExactNotification: exact, autoCancel: true } : {}),
      extra: { autodayId: n.id, kind: n.kind, pointIds: n.pointIds, open: n.open, sig: sig(n, hideSensitive, output, this.os), accountTag: this.accountTag },
    } as LocalNotificationSchema;
  }

  async apply(plan: NotificationPlan, opts: { hideSensitive: boolean; output?: NotificationOutput; activeTestId?: number }): Promise<ScheduleReport> {
    const errors: string[] = [];
    const output = opts.output ?? DEFAULT_NOTIFICATION_OUTPUT;
    await this.init(output);
    const st = await this.status();
    if (st.permission !== 'granted') {
      return { at: new Date().toISOString(), channel: this.channel, requested: plan.scheduled.length, accepted: [], verifiedPending: 0, inexact: false, errors: [tr('Notisbehörighet saknas.', 'Notification permission is missing.')], coverageUntil: null, overflowPoints: plan.overflowPoints };
    }
    const exact = st.exactAlarms !== 'denied';
    const pending = await LocalNotifications.getPending();
    const want = new Map(plan.scheduled.map((n) => [n.numericId, n]));
    // Avboka inaktuella (raderade, klara, ändrade).
    // Notiser som inte bär DENNA profils markering avbokas alltid: en annan profils (t.ex. ett utloggat
    // konto) och omärkta från äldre versioner. De som planen vill ha schemaläggs om med rätt markering.
    const toCancel = pending.notifications.filter((p) => {
      if (notificationTag(p) !== this.accountTag) return true;
      // Testet ligger utanför kalenderplanen. Behåll bara det senast aktiva testet när
      // synk, språkbyte eller en ny kalenderändring planerar om vanliga påminnelser.
      if (p.id === opts.activeTestId && (p.extra as { kind?: string } | undefined)?.kind === 'test') return false;
      const w = want.get(p.id);
      return !w || (p.extra as { sig?: string } | undefined)?.sig !== sig(w, opts.hideSensitive, output, this.os);
    });
    if (toCancel.length) await LocalNotifications.cancel({ notifications: toCancel.map((p) => ({ id: p.id })) });
    const keep = new Set(pending.notifications.filter((p) => !toCancel.includes(p)).map((p) => p.id));
    const toSchedule = plan.scheduled.filter((n) => !keep.has(n.numericId));
    let inexact = !exact;
    if (toSchedule.length) {
      try {
        const res = await LocalNotifications.schedule({ notifications: toSchedule.map((n) => this.toSchema(n, opts.hideSensitive, exact, output)) });
        if ((res as { warning?: string }).warning) {
          inexact = true;
          errors.push(String((res as { warning?: string }).warning));
        }
      } catch (e) {
        errors.push(tr('Schemaläggning misslyckades: ', 'Scheduling failed: ') + (e instanceof Error ? e.message : String(e)));
      }
    }
    const after = await LocalNotifications.getPending();
    const pendingIds = new Set(after.notifications.map((p) => p.id));
    const accepted = plan.scheduled.filter((n) => pendingIds.has(n.numericId)).map((n) => n.id);
    if (accepted.length < plan.scheduled.length) errors.push(tr(`${plan.scheduled.length - accepted.length} notiser saknas i systemets lista efter schemaläggning.`, `${plan.scheduled.length - accepted.length} notifications are missing from the system list after scheduling.`));
    return {
      at: new Date().toISOString(),
      channel: this.channel,
      requested: plan.scheduled.length,
      accepted,
      verifiedPending: accepted.length,
      inexact,
      errors,
      coverageUntil: plan.coverageUntil,
      overflowPoints: plan.overflowPoints,
    };
  }

  async scheduleTest(fireAt: Date, testId: string, selectedOutput?: NotificationOutput): Promise<{ ok: boolean; detail: string }> {
    const output = selectedOutput ?? DEFAULT_NOTIFICATION_OUTPUT;
    await this.init(output);
    const st = await this.status();
    if (st.permission !== 'granted') return { ok: false, detail: tr('Notisbehörighet saknas.', 'Notification permission is missing.') };
    const id = stableInt31('test:' + testId);
    await LocalNotifications.schedule({
      notifications: [
        {
          id,
          title: tr('AutoDay testnotis', 'AutoDay test notification'),
          body: tr('Om du ser detta med låst skärm fungerar lokala påminnelser. Öppna appen och bekräfta.', 'If you see this with the screen locked, local reminders work. Open the app and confirm.'),
          schedule: { at: fireAt, allowWhileIdle: true },
          actionTypeId: ACTION_TYPE,
          extra: { autodayId: 'test:' + testId, kind: 'test', accountTag: this.accountTag },
          ...(this.os === 'android' ? { channelId: channelId(false, output), sound: output.sound ? 'autoday_chime.wav' : 'autoday_silent.wav', isExactNotification: st.exactAlarms === 'granted' } : {}),
        } as LocalNotificationSchema,
      ],
    });
    const pending = await LocalNotifications.getPending();
    const ok = pending.notifications.some((p) => p.id === id);
    return { ok, detail: ok ? tr(`Schemalagd i systemet${st.exactAlarms === 'denied' ? ' (inexakt larm)' : ''}. Lås skärmen och vänta.`, `Scheduled in the system${st.exactAlarms === 'denied' ? ' (inexact alarm)' : ''}. Lock the screen and wait.`) : tr('Systemet visar inte testnotisen som schemalagd.', 'The system does not list the test notification as scheduled.') };
  }
}

/** Profilmarkeringen i en lokal notis (saknas i notiser från versioner före profilerna). */
export function notificationTag(n: { extra?: unknown }): string | undefined {
  const t = (n.extra as { accountTag?: unknown } | undefined)?.accountTag;
  return typeof t === 'string' ? t : undefined;
}

/**
 * Utloggning på enheten: avboka kontots väntande lokala notiser och ta bort dess visade. Omärkta notiser
 * (äldre versioner) kan inte knytas till en profil och tas också bort – gästprofilen schemalägger sina
 * egna igen vid nästa start. Notiser med andra markeringar (gästens, andra konton) lämnas orörda.
 */
export async function clearAccountNotifications(tags: string[]): Promise<{ cancelled: number; removed: number }> {
  const match = (n: { extra?: unknown }) => {
    const t = notificationTag(n);
    return t === undefined || tags.includes(t);
  };
  const pending = await LocalNotifications.getPending().catch(() => ({ notifications: [] as Array<{ id: number; extra?: unknown }> }));
  const mine = pending.notifications.filter(match);
  if (mine.length) await LocalNotifications.cancel({ notifications: mine.map((p) => ({ id: p.id })) }).catch(() => undefined);
  const delivered = await LocalNotifications.getDeliveredNotifications().catch(() => ({ notifications: [] as Awaited<ReturnType<typeof LocalNotifications.getDeliveredNotifications>>['notifications'] }));
  const shown = delivered.notifications.filter(match);
  if (shown.length) await LocalNotifications.removeDeliveredNotifications({ notifications: shown }).catch(() => undefined);
  return { cancelled: mine.length, removed: shown.length };
}

function sig(n: PlannedNotification, hideSensitive: boolean, output: NotificationOutput, os: 'android' | 'ios'): string {
  return `${n.fireAt}|${n.title}|${n.body}|${n.sensitive ? 1 : 0}|${hideSensitive ? 1 : 0}|${os === 'android' ? channelId(n.sensitive && hideSensitive, output) : 'ios'}`;
}
